import type { RawExit, LogicData } from '../types';
import { REGION_TO_ZONE } from './region-zone-map';
import rawExits from './overworld-exits.json';
import rawHelpers from './logic-helpers.json';

/**
 * Filters overworld exits to only cross-zone transitions.
 * Exits between regions in the same zone are intra-zone (free navigation).
 */
function buildCrossZoneExits(exits: RawExit[]): RawExit[] {
  return exits.filter((exit) => {
    const fromZone = REGION_TO_ZONE[exit.from];
    const toZone = REGION_TO_ZONE[exit.to];
    // Keep only if both regions are mapped AND they're in different zones
    if (!fromZone || !toZone) return false;
    // Skip internal zones
    if (fromZone.startsWith('_') || toZone.startsWith('_')) return false;
    return fromZone !== toZone;
  });
}

/** Pre-computed logic data singleton */
let cached: LogicData | null = null;

export function getLogicData(): LogicData {
  if (cached) return cached;

  const exits = buildCrossZoneExits(rawExits as RawExit[]);
  const helpers = rawHelpers as Record<string, string>;

  cached = { exits, helpers };
  return cached;
}

/**
 * Resolve the zone code for an OoTR region name.
 * Returns undefined if the region is not mapped.
 */
export function resolveZone(regionName: string): string | undefined {
  return REGION_TO_ZONE[regionName];
}

/**
 * Default settings for expression evaluation.
 * These represent "standard" randomizer settings.
 */
export const DEFAULT_SETTINGS: Record<string, unknown> = {
  open_forest: 'open',
  open_kakariko: 'open',
  open_door_of_time: true,
  zora_fountain: 'closed',
  gerudo_fortress: 'fast',
  bridge: 'medallions',
  shuffle_overworld_entrances: true,
  shuffle_dungeon_entrances: false,
  shuffle_interior_entrances: false,
  shuffle_grotto_entrances: false,
  free_bombchu_drops: false,
  starting_age: 'child',
  damage_multiplier: 'normal',
  shuffle_individual_ocarina_notes: false,
  logic_grottos_without_agony: true,
  shuffle_gerudo_fortress_heart_piece: 'vanilla',
  shuffle_hideout_entrances: false,
  skip_child_zelda: false,
  skip_reward_from_rauru: false,
  nnn: false,
  blue_fire_arrows: false,
  can_oi: false,
};
